
import { of as observableOf,  Subject ,  Observable } from 'rxjs'

import { mergeMap,  catchError } from 'rxjs/operators'
import { useState, useRef, useEffect } from 'react'
import { IQuote } from 'admin/interfaces'
import { useDispatch } from 'react-redux'
import { StoreThunkDispatch } from 'store/state'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import styles from './Statuses.pcss'
import { EditableQuote } from './EditableQuote'
import { message } from 'services/snackbar'
import { deleteStatus, getTags, updateStatus } from 'services/content/statuses/actions'

export function StatusesTable(props: { statuses: IQuote[] }) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [loading, setLoading] = useState(false)
  const update$ = useRef<Subject<{ id: string, status: string, author?: string, tags?: string[] }>>(undefined)
  const delete$ = useRef<Subject<string>>(undefined)
  const [statuses, setStatuses] = useState(props.statuses)
  const [tags, setTags] = useState<string[]>([])

  useEffect(() => {
    dispatch(getTags(true)).subscribe((response) => {
      setTags(response.map((t: any) => t.name))
    }, (error) => {
      dispatch(message('Failed to fetch tags: ', error.message))
    })
  }, [dispatch])

  useEffect(() => {
    setStatuses(props.statuses)
  }, [props.statuses])

  useEffect(() => {
    delete$.current = new Subject()
    delete$.current.pipe(mergeMap((id: string) => dispatch(deleteStatus(id)).pipe(
      catchError((e: Error) => {
        dispatch(message(`Error deleting object: ${e.message}`))
        return observableOf({ error: true })
      })
    )))
      .subscribe((response) => {
        if (!response.error) {
          dispatch(message('Status deleted!'))
          setStatuses(current => current.filter(q => q.id !== response.id))
        }
        setLoading(false)
      })

    return () => delete$.current?.unsubscribe()
  }, [dispatch])

  useEffect(() => {
    update$.current = new Subject()
    update$.current.pipe(mergeMap((params: { id: string, status: string, author?: string, tags?: string[] }) => {
      return dispatch(updateStatus(params.id, params.status, params.author, params.tags)).pipe(
        catchError((e: Error) => {
          dispatch(message(`Error updating object: ${e.message}`))
          return observableOf({ error: true })
        })
      )
    }))
      .subscribe((response) => {
        if (!response.error) {
          dispatch(message('Status updated!'))
        }
        setLoading(false)
      })

    return () => update$.current?.unsubscribe()
  }, [dispatch])

  const onDelete = (id: string) => {
    setLoading(true)
    delete$.current?.next(id)
  }

  const onUpdate = (id: string, status: string, author?: string, newTags?: string[]) => {
    setLoading(true)
    update$.current?.next({ id, status, author, tags: newTags })
  }

  return (
    <div className={styles.wrapper}>
      <Backdrop open={loading} className={styles.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
      {statuses.map(q => (
        <EditableQuote
          key={q.id}
          quote={q}
          savedTags={tags}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  )
}
